import './QAMessageListView';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { trackEvent } from '../../extra/sharedMethods';
import { invokeFastApi } from '../../scripts/apis/fastapi';
import { AnalyticsEvent } from '../../scripts/constants/analytics-event';
import { useToaster } from '../../scripts/hooks';
import { UIIcon } from '../controls/ui/UIIcon/UIIcon';

interface QATopicDividerProps {
  conversationId?: string;
  isEditable?: boolean;
  isNewTopic: boolean;
  topicTitle?: string;
  topicTimestamp?: number;
}

export const QATopicDivider: FC<QATopicDividerProps> = ({
  conversationId,
  isEditable,
  isNewTopic,
  topicTitle,
  topicTimestamp,
}: QATopicDividerProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState(topicTitle);
  const divRef = useRef<HTMLDivElement>(null);
  const toaster = useToaster();

  useEffect(() => {
    if (!topicTitle && divRef.current) {
      divRef.current.textContent = 'New Topic';
    }

    setNewTopicTitle(topicTitle);
  }, [topicTitle]);

  useEffect(() => {
    if (isEditing && divRef.current) {
      divRef.current.focus();
      const range = document.createRange();
      range.selectNodeContents(divRef.current);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }, [isEditing]);

  const handleUpdateTopicTitle = useCallback(async () => {
    setIsEditing(false);

    if (!conversationId || topicTitle === newTopicTitle) return;

    if (newTopicTitle === '' && divRef.current) {
      setNewTopicTitle(topicTitle);
      divRef.current.textContent = topicTitle ?? 'Example Topic Title';
      return;
    }

    try {
      await invokeFastApi({
        path: `/topics/topicTitle/${conversationId}`,
        method: 'PATCH',
        body: { topic_title: newTopicTitle },
      });
    } catch {
      toaster.failure('Failed to update topic title');
    }
  }, [conversationId, newTopicTitle, toaster, topicTitle]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (divRef.current) {
          divRef.current.blur();
        }
      }
    },
    []
  );

  const handleCopyLink = useCallback(() => {
    if (!conversationId) {
      toaster.failure('Failed to copy shareable link');
      return;
    }

    const sharableLink = `${window.location.origin}/topic/${conversationId}`;
    navigator.clipboard.writeText(sharableLink);
    toaster.success('Copied shareable link to clipboard');
    trackEvent(AnalyticsEvent.SharableLinkCreated);
  }, [conversationId, toaster]);

  return (
    <div className="relative bg-white pt-[26px] pb-6">
      <div className="border-[0.5px] border-solid border-cloud-15" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex gap-1 px-4 bg-white whitespace-nowrap">
        {isNewTopic ? (
          <div className="text-amethys-10">New Topic</div>
        ) : (
          <>
            {isEditable ? (
              <div
                className={`outline-none text-amethys-10 hidden sm:block  ${
                  isEditing ? 'cursor-text' : 'cursor-pointer max-w-[300px]'
                }`}
                contentEditable={isEditing}
                onBlur={handleUpdateTopicTitle}
                onClick={() => {
                  setIsEditing(true);
                }}
                onInput={(e) => {
                  setNewTopicTitle(e.currentTarget.textContent ?? topicTitle);
                }}
                onKeyDown={handleKeyDown}
                ref={divRef}
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                suppressContentEditableWarning
              >
                {topicTitle}
              </div>
            ) : (
              <div>{topicTitle}</div>
            )}
            <div className="text-cloud-20 hidden sm:block">|</div>
            <div className="font-light">
              {moment(topicTimestamp).format('MMM D, h:mm a')}
            </div>
            <div onClick={handleCopyLink}>
              <div className="cursor-pointer ml-2 border border-cloud-15 border-solid shadow-flat flex bg-white rounded p-1 hover:bg-cloud-15">
                <UIIcon name="share" tooltip="Share a link to this topic" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
